"use client";
import type { LinkComponentType } from "../../../index";
import { SiteMetaDisplay } from "../../SiteMetaDisplay";
import type { CellComponentProps, CellEmphasis } from "../DataTable.types";
import { getCellContainerClasses, resolveValue } from "./cellUtils";

export interface SiteMetaCellProps<T = unknown> extends CellComponentProps<T> {
  /** Street address (can be a function to extract from row) */
  streetAddress: string | ((row: T) => string);

  /** City name (optional, can be a function to extract from row) */
  city?: string | ((row: T) => string | undefined);

  /** State/province (optional, can be a function to extract from row) */
  state?: string | ((row: T) => string | undefined);

  /** Postal/ZIP code (optional, can be a function to extract from row) */
  postalCode?: string | ((row: T) => string | undefined);

  /** Layout variant - stacked (vertical) or inline (horizontal) */
  layout?: "stacked" | "inline";

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Link to site detail page (optional) */
  href?: string | ((row: T) => string);

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Link variant for the street address */
  linkVariant?: "default" | "brand" | "muted" | "unstyled";

  /** Text emphasis level - "strong"/"high" for bold, "normal" for regular, "low" for muted */
  emphasis?: CellEmphasis;

  /** Alignment */
  align?: "left" | "center" | "right";

  /** Additional classes */
  className?: string;

  /**
   * Entity identifier for PII masking (can be a function to extract from row)
   * Example: (row) => `site:${row.id}`
   */
  piiEntity?: string | ((row: T) => string | undefined);
}

/**
 * SiteMetaCell
 *
 * DataTable cell component for displaying site location information.
 * Wraps SiteMetaDisplay for use in DataTable columns with layout="stacked" by default.
 *
 * @example
 * ```tsx
 * // In DataTable column definition
 * {
 *   id: "site",
 *   label: "Site",
 *   cell: SiteMetaCell,
 *   cellProps: {
 *     streetAddress: (row) => row.location.streetOne,
 *     city: (row) => row.location.city,
 *     state: (row) => row.location.state,
 *     postalCode: (row) => row.location.postalCode,
 *     href: (row) => `/sites/${row.id}`,
 *     layout: "stacked",
 *     size: "sm"
 *   }
 * }
 * ```
 */
export function SiteMetaCell<T = unknown>({
  row,
  context,
  streetAddress,
  city,
  state,
  postalCode,
  layout = "stacked",
  size = "sm",
  href,
  LinkComponent,
  linkVariant = "default",
  emphasis = "normal",
  align = "left",
  className,
  piiEntity,
}: SiteMetaCellProps<T>) {
  const { isLoading } = context;

  // Resolve dynamic values
  const streetAddressValue = resolveValue(streetAddress, row);
  const cityValue = resolveValue(city, row);
  const stateValue = resolveValue(state, row);
  const postalCodeValue = resolveValue(postalCode, row);
  const hrefValue = resolveValue(href, row);
  const piiEntityValue = resolveValue(piiEntity, row);

  const containerClasses = getCellContainerClasses(align, className);

  return (
    <div className={containerClasses}>
      <SiteMetaDisplay
        streetAddress={streetAddressValue}
        city={cityValue}
        state={stateValue}
        postalCode={postalCodeValue}
        layout={layout}
        size={size}
        href={hrefValue}
        LinkComponent={LinkComponent}
        linkVariant={linkVariant}
        emphasis={emphasis}
        isLoading={isLoading}
        piiEntity={piiEntityValue}
      />
    </div>
  );
}
